/**
 * The board/timeline "Drops" sentence of one container (Phase 21: one list
 * of drops). One group reads as before — "5 × 500 → 2 × 1,000 · every 35
 * min"; several groups name each group's count — "12 times · clickers 8 ·
 * FTDs 7 · 12 × 802 · every 58 min"; pinned drops and drops without a group
 * are counted; Phase 33 adds the days it runs ("Fri–Sat") and the weekend
 * share ("weekends 60 %") when they are not the whole week at 100 %.
 * For Media Devoted | Created By Ciro Autiero
 */
import { describeRunDays, describeWeekendShare } from '@trafficpulse/shared';

interface DescribedSlot {
  amount: number | null;
  enabled: boolean;
  listGroupIds?: string[];
  pinnedAtMin?: number | null;
}

/** "5 × 500 → 2 × 1,000 · every 35 min" — a list's amounts and pace. */
export function describeDrops(slots: DescribedSlot[], defaultAmount: number, intervalMin: number): string {
  const active = slots.filter((s) => s.enabled);
  if (active.length === 0) return 'no drops';
  return `${amountRuns(active, defaultAmount)} · every ${intervalMin} min`;
}

/** "5 × 500 → 2 × 1,000": consecutive equal amounts collapsed into runs. */
export function amountRuns(slots: Array<{ amount: number | null }>, defaultAmount: number): string {
  const runs: Array<{ amount: number; count: number }> = [];
  for (const slot of slots) {
    const amount = slot.amount ?? defaultAmount;
    const last = runs[runs.length - 1];
    if (last && last.amount === amount) last.count += 1;
    else runs.push({ amount, count: 1 });
  }
  return runs.map((r) => `${r.count} × ${r.amount.toLocaleString('en-US')}`).join(' → ');
}

export interface DescribedContainer {
  defaultAmount: number;
  intervalMin: number;
  slots: DescribedSlot[];
  /** Linked groups in link order. */
  groups?: Array<{ listGroupId: string; groupName: string }>;
  /** Phase 33 — absent = every day, 100 %. */
  runDays?: readonly string[];
  weekendAmountPct?: number;
}

export function describeContainerDrops(c: DescribedContainer): string {
  const days = describeRunDays(c.runDays);
  return [dropsSentence(c), days === 'every day' ? '' : days, describeWeekendShare(c.runDays, c.weekendAmountPct) ?? '']
    .filter(Boolean)
    .join(' · ');
}

function dropsSentence(c: DescribedContainer): string {
  const groups = c.groups ?? [];
  const active = c.slots.filter((s) => s.enabled);
  const base = describeDrops(c.slots, c.defaultAmount, c.intervalMin);
  if (active.length === 0 || groups.length === 0) return base;
  const count = (id: string) => active.filter((s) => (s.listGroupIds ?? []).includes(id)).length;
  const linked = new Set(groups.map((g) => g.listGroupId));
  const bare = active.filter((s) => !(s.listGroupIds ?? []).some((g) => linked.has(g))).length;
  const pinned = active.filter((s) => s.pinnedAtMin !== null && s.pinnedAtMin !== undefined).length;
  const parts = [
    groups.length > 1 ? `${active.length} times · ${groups.map((g) => `${g.groupName} ${count(g.listGroupId)}`).join(' · ')}` : '',
    base,
    pinned > 0 ? `${pinned} pinned` : '',
    bare > 0 ? `${bare} without a group` : '',
  ];
  return parts.filter(Boolean).join(' · ');
}
